// EmployerOverview.jsx
// In EmployerDashboard.jsx:
//   import EmployerOverview from './EmployerOverview'
//   {nav === 'overview' && <EmployerOverview t={t} employees={employees} checkIns={checkIns} todayIns={todayIns} allMeetings={allMeetings} pdrCounts={pdrCounts} profile={profile} />}

import { useState } from 'react'

function moodEmoji(m) { return { great:'😄', good:'🙂', okay:'😐', tough:'😔' }[m] || '—' }
function moodColor(m) { return { great:'#059669', good:'#4F46E5', okay:'#D97706', tough:'#EF4444' }[m] || '#64748B' }
function moodBg(m)    { return { great:'#ECFDF5', good:'#EFF6FF', okay:'#FFFBEB', tough:'#FEF2F2' }[m] || '#F8F9FA' }
function getTime()    { const h = new Date().getHours(); return h<12?'morning':h<17?'afternoon':'evening' }

function getLast7Days() {
  return Array.from({ length:7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i))
    return { date: d.toISOString().split('T')[0], label: d.toLocaleDateString('en-US',{ weekday:'short' }) }
  })
}

export default function EmployerOverview({ t, employees, checkIns, todayIns, allMeetings, pdrCounts, profile }) {
  const today   = new Date().toISOString().split('T')[0]
  const last7   = getLast7Days()

  // Stats
  const totalPending = Object.values(pdrCounts || {}).reduce((s,c) => s + c.pending, 0)
  const checkedOutToday = checkIns.filter(c => c.date === today && c.checkout_submitted_at).length
  const meetingsPending = (allMeetings || []).reduce((s,m) => s + (m.structured_action_items||[]).filter(i=>!i.completed).length, 0)

  // Mood distribution today
  const todayMoods = {}
  todayIns.forEach(c => {
    const m = c.checkin_mood
    if (m) todayMoods[m] = (todayMoods[m] || 0) + 1
  })

  // Check-in rate last 7 days
  const checkInByDay = last7.map(day => ({
    ...day,
    count: checkIns.filter(c => c.date === day.date).length,
    total: employees.length,
  }))

  // Recent blockers across all employees
  const recentBlockers = checkIns
    .filter(c => c.blockers && c.blockers.trim())
    .slice(0, 8)

  // Employee name lookup
  const empMap = Object.fromEntries(employees.map(e => [e.id, e]))

  // Top absent employees (checked in least this week)
  const weekDates = new Set(last7.map(d => d.date))
  const empCheckInCount = employees.map(e => ({
    emp: e,
    count: checkIns.filter(c => c.employee_id === e.id && weekDates.has(c.date)).length,
  })).sort((a, b) => a.count - b.count)

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom:'1.75rem' }}>
        <h1 style={{ fontSize:26, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>
          Good {getTime()} 👋
        </h1>
        <p style={{ fontSize:15, color:t.textMuted, margin:0 }}>
          {new Date().toLocaleDateString('en-US',{ weekday:'long', month:'long', day:'numeric' })} · Here's your team overview
        </p>
      </div>

      {/* Key stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:'1.75rem' }}>
        {[
          { label:'Total Employees',    value:employees.length,   color:'#4F46E5', icon:'👥', sub:'in your org' },
          { label:'Checked In Today',   value:todayIns.length,    color:'#059669', icon:'✅', sub:`${checkedOutToday} checked out` },
          { label:'Pending PDR Reviews',value:totalPending,       color:'#D97706', icon:'📋', sub:'awaiting approval' },
          { label:'Action Items Open',  value:meetingsPending,    color:'#7C3AED', icon:'🗓', sub:'across all meetings' },
        ].map(s => (
          <div key={s.label} style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
              <div style={{ fontSize:28, fontWeight:900, color:s.color }}>{s.value}</div>
              <div style={{ width:38, height:38, borderRadius:9, background:`${s.color}14`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>{s.icon}</div>
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:t.text }}>{s.label}</div>
            <div style={{ fontSize:11, color:t.textMuted, marginTop:2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

        {/* Check-in rate chart */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', boxShadow:t.shadow }}>
          <div style={{ fontSize:15, fontWeight:700, color:t.text, marginBottom:'1.25rem' }}>📊 Check-in Rate — Last 7 Days</div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:100, marginBottom:8 }}>
            {checkInByDay.map((day, i) => {
              const pct  = day.total ? Math.round((day.count / day.total) * 100) : 0
              const isToday = day.date === today
              return (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <div style={{ fontSize:10, color:t.textMuted, marginBottom:2 }}>{pct}%</div>
                  <div style={{ width:'100%', height:70, borderRadius:'6px 6px 0 0', background:isToday?'linear-gradient(135deg,#4F46E5,#6366F1)':'#E5E7EB', position:'relative', overflow:'hidden' }}>
                    {!isToday && <div style={{ position:'absolute', bottom:0, width:'100%', height:`${pct}%`, background:'linear-gradient(135deg,#4F46E5,#6366F1)' }} />}
                    {isToday && <div style={{ position:'absolute', bottom:0, width:'100%', height:`${pct}%`, background:'rgba(255,255,255,0.3)' }} />}
                  </div>
                  <span style={{ fontSize:10, color:isToday?'#4F46E5':t.textMuted, fontWeight:isToday?700:400 }}>{day.label}</span>
                </div>
              )
            })}
          </div>
          <div style={{ fontSize:12, color:t.textSub }}>
            <strong style={{ color:t.text }}>{todayIns.length}</strong> of {employees.length} employees checked in today
          </div>
        </div>

        {/* Team mood today */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', boxShadow:t.shadow }}>
          <div style={{ fontSize:15, fontWeight:700, color:t.text, marginBottom:'1.25rem' }}>😊 Team Mood Today</div>
          {Object.keys(todayMoods).length === 0 ? (
            <p style={{ fontSize:13, color:t.textMuted }}>No check-ins yet today.</p>
          ) : (
            <>
              {['great','good','okay','tough'].filter(m => todayMoods[m]).map(m => {
                const count = todayMoods[m] || 0
                const pct = todayIns.length ? Math.round((count/todayIns.length)*100) : 0
                return (
                  <div key={m} style={{ marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:18 }}>{moodEmoji(m)}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:moodColor(m), textTransform:'capitalize' }}>{m}</span>
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, color:moodColor(m) }}>{count} ({pct}%)</span>
                    </div>
                    <div style={{ background:t.border, borderRadius:99, height:8 }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:moodColor(m), borderRadius:99 }} />
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

        {/* Recent blockers/hurdles */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', boxShadow:t.shadow }}>
          <div style={{ fontSize:15, fontWeight:700, color:t.text, marginBottom:'1.25rem' }}>⚠️ Recent Hurdles Across Team</div>
          {recentBlockers.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1rem 0' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
              <p style={{ fontSize:13, color:'#059669', fontWeight:600 }}>No blockers reported recently!</p>
            </div>
          ) : recentBlockers.map(c => {
            const emp = empMap[c.employee_id]
            return (
              <div key={c.id} style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:`1px solid ${t.border}` }}>
                <div style={{ width:30, height:30, borderRadius:'50%', background:'#FEF2F2', border:'1px solid #FECACA', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#EF4444', flexShrink:0 }}>
                  {emp?.full_name?.charAt(0) || '?'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:t.text }}>{emp?.full_name || 'Unknown'} · <span style={{ color:t.textMuted, fontWeight:400 }}>{c.date}</span></div>
                  <p style={{ fontSize:12, color:'#DC2626', margin:0, lineHeight:1.5 }}>{c.blockers}</p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Attendance this week */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', boxShadow:t.shadow }}>
          <div style={{ fontSize:15, fontWeight:700, color:t.text, marginBottom:'1.25rem' }}>📅 Attendance This Week</div>
          {employees.length === 0 ? (
            <p style={{ fontSize:13, color:t.textMuted }}>No employees yet.</p>
          ) : empCheckInCount.slice(0, 8).map(({ emp, count }) => {
            const pct = last7.length ? Math.round((count / last7.length) * 100) : 0
            return (
              <div key={emp.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom:`1px solid ${t.border}` }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
                  {emp.full_name?.charAt(0)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emp.full_name}</div>
                  <div style={{ background:t.border, borderRadius:99, height:5, marginTop:3 }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:pct>=80?'#059669':pct>=50?'#4F46E5':'#EF4444', borderRadius:99 }} />
                  </div>
                </div>
                <span style={{ fontSize:12, fontWeight:700, color:pct>=80?'#059669':pct>=50?'#4F46E5':'#EF4444', flexShrink:0 }}>{count}/{last7.length}d</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
